import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import useCampaign from '../hooks/useCampaign.js';

const EXAMPLE_PROMPTS = [
  '$500 tee box sponsors for a golf tournament drawing Biltmore/Arcadia families and AEC professionals',
  'High-end salons and spas in the Biltmore corridor that might donate raffle prizes (~$250 value)',
  'Commercial real estate firms and wealth managers who could be $5K title sponsors for a nonprofit fundraiser',
  'Family-owned restaurants that might provide catering for a board dinner',
];

export default function IdeateDialog({ open, onClose }) {
  const { campaignId } = useCampaign();
  const queryClient = useQueryClient();
  const [prompt, setPrompt] = useState('');
  const [status, setStatus] = useState(null); // {phase, message}
  const [intent, setIntent] = useState(null);
  const [candidates, setCandidates] = useState(null); // {count, sample_ids}
  const [result, setResult] = useState(null); // final 'done' payload
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [addedIds, setAddedIds] = useState(new Set());
  const accumulatedRef = useRef('');

  const addProspectMutation = useMutation({
    mutationFn: ({ businessId }) =>
      api.createProspect({
        campaign_id: campaignId,
        business_id: businessId,
        status: 'identified',
        notes: `Added via AI ideate: ${prompt.substring(0, 200)}`,
      }),
    onSuccess: (_, { businessId }) => {
      setAddedIds((prev) => new Set(prev).add(businessId));
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      queryClient.invalidateQueries({ queryKey: ['campaignStats'] });
    },
  });

  function resetState() {
    setStatus(null);
    setIntent(null);
    setCandidates(null);
    setResult(null);
    setError(null);
    setAddedIds(new Set());
    accumulatedRef.current = '';
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!prompt.trim() || streaming) return;

    resetState();
    setStreaming(true);

    try {
      await api.ideateStream(prompt, {
        onStatus: (p) => setStatus(p),
        onIntent: (p) => setIntent(p),
        onCandidates: (p) => setCandidates(p),
        onChunk: (p) => {
          accumulatedRef.current += p.text;
        },
        onDone: (p) => {
          setResult(p);
          setStatus(null);
        },
        onError: (p) => setError(p.message || 'Ideate failed'),
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setStreaming(false);
    }
  }

  if (!open) return null;

  const showExamples = !streaming && !result && !intent;
  const showInProgress = streaming || (intent && !result);

  return (
    <div className="fixed inset-0 z-[2000] bg-black/40 flex items-stretch justify-end" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-white shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-200 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Ask AI for Prospects</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Describe the prospect profile you're looking for. The AI will search and rank businesses.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">
            ×
          </button>
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-4 border-b border-gray-200">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            placeholder="e.g. $500 tee box sponsors for a golf tournament drawing Biltmore/Arcadia families and AEC professionals"
            className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ccf-primary resize-none"
            disabled={streaming}
          />

          {showExamples && (
            <div className="mt-2">
              <div className="text-xs text-gray-500 mb-1">Try an example:</div>
              <div className="flex flex-wrap gap-1">
                {EXAMPLE_PROMPTS.map((ex, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPrompt(ex)}
                    className="text-xs bg-gray-100 text-gray-700 rounded px-2 py-1 hover:bg-gray-200 text-left"
                  >
                    {ex.length > 50 ? ex.substring(0, 50) + '...' : ex}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3 flex items-center gap-3">
            <button
              type="submit"
              disabled={streaming || !prompt.trim()}
              className="bg-ccf-primary text-white text-sm rounded px-4 py-2 hover:bg-blue-900 disabled:opacity-50"
            >
              {streaming ? 'Thinking...' : 'Ideate'}
            </button>
            {(result || intent) && !streaming && (
              <button
                type="button"
                onClick={() => {
                  resetState();
                }}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                New search
              </button>
            )}
            {result?.timing_ms && (
              <div className="text-xs text-gray-400 ml-auto">
                {(result.timing_ms.total / 1000).toFixed(1)}s
              </div>
            )}
          </div>
        </form>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {showInProgress && (
            <div className="space-y-3 mb-4">
              {status && (
                <div className="text-sm text-gray-600 animate-pulse">{status.message}</div>
              )}
              {intent && (
                <div className="bg-blue-50 border border-blue-100 rounded p-3">
                  <div className="text-xs font-semibold text-blue-900 uppercase mb-1">AI Interpretation</div>
                  <div className="text-sm text-blue-900">{intent.intent_summary}</div>
                </div>
              )}
              {candidates && (
                <div className="text-xs text-gray-500">
                  Found {candidates.count} candidates. Ranking now...
                </div>
              )}
              {streaming && accumulatedRef.current.length > 0 && (
                <div className="text-xs text-gray-400 font-mono whitespace-pre-wrap border border-gray-100 rounded p-2 max-h-32 overflow-y-auto">
                  {accumulatedRef.current.slice(-400)}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
              Error: {error}
            </div>
          )}

          {result && result.candidate_count === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
              No businesses matched those filters. Try a broader query.
            </div>
          )}

          {result && result.tiers && result.tiers.length > 0 && (
            <div>
              {/* Intent summary */}
              <div className="mb-4 bg-blue-50 border border-blue-100 rounded p-3">
                <div className="text-xs font-semibold text-blue-900 uppercase mb-1">AI Interpretation</div>
                <div className="text-sm text-blue-900">{result.intent_summary}</div>
                {result.overall_strategy && (
                  <div className="text-xs text-blue-800 mt-2 italic">{result.overall_strategy}</div>
                )}
                <div className="text-xs text-blue-700 mt-2">
                  Searched {result.candidate_count} candidates
                </div>
              </div>

              {/* Tiers */}
              {result.tiers.map((tier) => (
                <div key={tier.tier} className="mb-5">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-xs font-bold bg-ccf-accent text-white rounded px-2 py-0.5">
                      TIER {tier.tier}
                    </span>
                    <h3 className="font-semibold text-gray-900">{tier.label}</h3>
                  </div>
                  {tier.reasoning && (
                    <p className="text-xs text-gray-600 italic mb-2">{tier.reasoning}</p>
                  )}

                  <div className="space-y-2">
                    {tier.businesses.map((b) => (
                      <div
                        key={b.id}
                        className="border border-gray-200 rounded p-3 hover:bg-gray-50"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <Link
                              to={`/business/${b.id}`}
                              className="font-semibold text-ccf-primary hover:underline"
                            >
                              {b.name}
                            </Link>
                            <div className="text-xs text-gray-500">
                              {b.primary_sic_description} · {b.corridor?.replace('_', ' ')}
                            </div>
                            <div className="text-xs text-gray-500">
                              {b.annual_revenue && `$${(b.annual_revenue / 1000).toFixed(0)}K revenue`}
                              {b.credit_rating && ` · Credit ${b.credit_rating}`}
                              {b.contact_name && ` · ${b.contact_name}`}
                            </div>
                            {b.pitch_hook && (
                              <div className="text-sm text-gray-800 mt-2 italic">
                                "{b.pitch_hook}"
                              </div>
                            )}
                          </div>
                          <div className="flex-shrink-0">
                            {addedIds.has(b.id) ? (
                              <span className="text-xs bg-emerald-100 text-emerald-700 rounded px-2 py-1">
                                ✓ Added
                              </span>
                            ) : (
                              <button
                                onClick={() =>
                                  addProspectMutation.mutate({ businessId: b.id })
                                }
                                disabled={!campaignId || addProspectMutation.isPending}
                                className="text-xs bg-ccf-primary text-white rounded px-3 py-1 hover:bg-blue-900 disabled:opacity-50"
                              >
                                + Campaign
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
